import {
  projectSidebarThreadGroups,
  sidebarDropAssignments,
  sidebarThreadKey as keyOf,
  type SidebarThreadGroups as Groups,
  type SidebarThreadMove,
} from "./SidebarThreadList.logic";
import type { ThreadSidebarOperation } from "@amoreg/contracts";
// Sidebar motion and drop planning are adapted from pingdotgg/t3code (MIT).
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDownIcon } from "lucide-react";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
  type AtomCommandResult,
} from "@amoreg/client-runtime/state/runtime";
import type { ExecutionEnvironmentCapabilities } from "@amoreg/contracts";
import { useThreadActions } from "../hooks/useThreadActions";
import { cn } from "../lib/utils";
import { toastManager } from "./ui/toast";
import { createSidebarListMotion } from "./Sidebar.motion";
import {
  createSidebarCollisionDetection,
  createSidebarSortingStrategy,
  restrictBelowSidebarLabel,
  sidebarDragTargetId,
} from "./Sidebar.drag";
import {
  animateSidebarLayoutChanges,
  applySidebarThreadDrop,
  planSidebarThreadDrop,
  resolveSidebarDropTarget,
  sortSettledThreadsForSidebar,
  sidebarListItemId,
  sidebarMarkerId,
  type SidebarListMarker,
  type SidebarListItem,
  type SidebarSection,
  type SidebarThreadDropPlan,
} from "./Sidebar.logic";

export type SortableThreadRowBag = Pick<
  ReturnType<typeof useSortable>,
  "listeners" | "setNodeRef" | "transform" | "transition" | "isDragging"
>;
type Hold = SidebarThreadMove & {
  baseline: ReadonlyMap<string, string | null>;
  pending: boolean;
};

function SortableThreadRow(props: {
  id: string;
  disabled: boolean;
  children: (bag: SortableThreadRowBag) => ReactNode;
}) {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.id,
    disabled: { draggable: props.disabled },
    animateLayoutChanges: animateSidebarLayoutChanges,
  });
  const bag = useMemo(
    () => ({ listeners, setNodeRef, transform, transition, isDragging }),
    [listeners, setNodeRef, transform, transition, isDragging],
  );
  return props.children(bag);
}
function SortableSidebarMarker(props: {
  marker: SidebarListMarker;
  className?: string | undefined;
  children?: ReactNode;
  "data-testid"?: string;
}) {
  const { setNodeRef, transform, transition } = useSortable({
    id: sidebarMarkerId(props.marker),
    disabled: { draggable: true },
    animateLayoutChanges: animateSidebarLayoutChanges,
  });
  return (
    <li
      ref={setNodeRef}
      data-thread-selection-safe
      data-testid={props["data-testid"]}
      className={cn("list-none", props.className)}
      style={{
        transform: CSS.Translate.toString(transform),
        // A newly revealed target must not slide from its hidden position.
        transition: props.marker.endsWith("-placeholder") ? "none" : transition,
        visibility: transform?.scaleY === 0 ? "hidden" : undefined,
      }}
    >
      {props.children}
    </li>
  );
}

// Empty targets stay measurable without reserving space at rest. The sorting
// strategy opens their hint space during a drag.
function SidebarSectionPlaceholder(props: {
  marker: "active-placeholder" | "settled-placeholder";
  label: string;
  showHint: boolean;
  isDropTarget: boolean;
}) {
  return (
    <SortableSidebarMarker
      marker={props.marker}
      data-testid={`sidebar-${props.marker}`}
      className="relative mx-0.5 -mb-px h-0"
    >
      {props.showHint ? (
        <div
          className={cn(
            "absolute inset-x-0 top-0 flex h-9 items-center justify-center rounded-md border border-dashed border-sidebar-foreground/25 text-xs text-sidebar-foreground/80",
            props.isDropTarget && "border-primary/40 bg-primary/5 text-primary",
          )}
        >
          {props.label}
        </div>
      ) : null}
    </SortableSidebarMarker>
  );
}

// Zero-height markers reserve no label space at rest. During a drag the
// sorting strategy opens 24px for a 16px label with 4px clearance on each side.
const SIDEBAR_DRAG_LABEL_HEIGHT = 24;

function SidebarDragBoundary(props: {
  marker: "pinned-header" | "pinned-divider";
  label: string;
  visible: boolean;
  isDropTarget: boolean;
}) {
  return (
    <SortableSidebarMarker
      marker={props.marker}
      data-testid={`sidebar-${props.marker}`}
      className="pointer-events-none relative mx-0.5 -mb-px h-0"
    >
      {props.visible ? (
        <div className="sidebar-drag-boundary-label absolute inset-x-2 top-1 flex h-4 items-center gap-2">
          <span
            className={cn(
              "shrink-0 text-xs font-medium",
              props.isDropTarget ? "text-primary" : "text-sidebar-foreground/80",
            )}
          >
            {props.label}
          </span>
          <span
            aria-hidden
            className={cn(
              "h-px flex-1",
              props.isDropTarget ? "bg-primary/50" : "bg-sidebar-foreground/25",
            )}
          />
        </div>
      ) : null}
    </SortableSidebarMarker>
  );
}

// Shelf headers stay visible and keep their measured height while dragging.
function SidebarSectionHeader(props: {
  marker: "snoozed-header" | "settled-header";
  label: string;
  className?: string | undefined;
  // While dragging, the settled header reads at full strength and takes the
  // accent while the lifted row is over it.
  dragging?: boolean;
  isDropTarget?: boolean;
  toggle: { expanded: boolean; onToggle: () => void };
}) {
  const snoozed = props.marker === "snoozed-header";
  const className = cn(
    "flex h-full w-full items-center gap-1 rounded-lg px-2 text-left text-xs font-medium hover:bg-sidebar-row-hover",
    snoozed ? "text-blue-600 dark:text-blue-400" : "text-sidebar-muted-foreground",
    props.dragging && "text-sidebar-foreground/80",
    props.isDropTarget && "text-primary",
  );
  const content = (
    <>
      <span className="shrink-0">{props.label}</span>
      <ChevronDownIcon
        aria-hidden
        className={cn(
          "size-3 shrink-0 opacity-70 transition-transform",
          !props.toggle.expanded && "-rotate-90",
        )}
      />
    </>
  );
  return (
    <SortableSidebarMarker
      marker={props.marker}
      data-testid={`sidebar-${props.marker}`}
      className={cn("mx-0.5 h-7", props.className)}
    >
      <button
        type="button"
        onClick={props.toggle.onToggle}
        aria-expanded={props.toggle.expanded}
        data-testid={`sidebar-${snoozed ? "snoozed" : "settled"}-shelf-toggle`}
        className={cn(className, "cursor-pointer")}
      >
        {content}
      </button>
    </SortableSidebarMarker>
  );
}

export type SidebarThreadListProps = {
  scopeKey: string;
  groups: Groups;
  visibleSnoozed: readonly EnvironmentThreadShell[];
  visibleSettled: readonly EnvironmentThreadShell[];
  isSettled?: (thread: EnvironmentThreadShell) => boolean;
  capabilities: (thread: EnvironmentThreadShell) => ExecutionEnvironmentCapabilities | undefined;
  snoozedExpanded: boolean;
  settledExpanded: boolean;
  onToggleSnoozed: () => void;
  onToggleSettled: () => void;
  routeThreadKey: string | null;
  planForwardNavigation: (key: string) => (() => void) | null;
  renderThread: (
    thread: EnvironmentThreadShell,
    section: SidebarSection,
    sortable?: SortableThreadRowBag,
    destination?: string,
  ) => ReactNode;
  footer?: ReactNode;
};
type ActionName = "settleThread" | "applySidebarThreadOperations";
export type SidebarThreadCommands = {
  [K in ActionName]: (
    ...args: Parameters<ReturnType<typeof useThreadActions>[K]>
  ) => Promise<AtomCommandResult<unknown, unknown>>;
};
export function SidebarThreadList(props: SidebarThreadListProps) {
  const actions = useThreadActions();
  return <SidebarThreadListView {...props} actions={actions} />;
}
export function SidebarThreadListView(
  props: SidebarThreadListProps & { actions: SidebarThreadCommands },
) {
  const actions = props.actions;
  const [hold, setHold] = useState<Hold | null>(null);
  const [drag, setDrag] = useState<{ key: string; activationY: number | null } | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const currentRoute = useRef(props.routeThreadKey);
  currentRoute.current = props.routeThreadKey;
  const currentScope = useRef(props.scopeKey);
  currentScope.current = props.scopeKey;
  const motion = useRef<ReturnType<typeof createSidebarListMotion> | null>(null);
  const all = useMemo(() => Object.values(props.groups).flat(), [props.groups]);
  const byKey = useMemo(() => new Map(all.map((thread) => [keyOf(thread), thread])), [all]);
  useEffect(() => {
    setHold(null);
    setDrag(null);
    setOverId(null);
    motion.current?.suspend();
  }, [props.scopeKey]);
  useEffect(() => {
    if (!hold) return;
    const moved = byKey.get(hold.key);
    if (!moved) {
      setHold(null);
      return;
    }
    const orderKey = (thread: EnvironmentThreadShell) =>
      (hold.section === "pinned" ? thread.pinOrderKey : thread.activeOrderKey) ?? null;
    const foreignWrite = [...hold.baseline].some(([key, previous]) => {
      const row = byKey.get(key);
      return !row || (orderKey(row) !== previous && orderKey(row) !== hold.assigned.get(key));
    });
    const landed =
      props.groups[hold.section].some((thread) => keyOf(thread) === hold.key) &&
      [...hold.assigned].every(([key, value]) => {
        const row = byKey.get(key);
        return row && orderKey(row) === value;
      });
    if (foreignWrite || (!hold.pending && landed)) setHold(null);
  }, [hold, byKey, props.groups]);
  const groups = useMemo<Groups>(
    () => (hold ? projectSidebarThreadGroups(props.groups, hold) : props.groups),
    [hold, props.groups],
  );
  const visibleSnoozed = useMemo(() => {
    const visible = new Set(props.visibleSnoozed.map(keyOf));
    return groups.snoozed.filter((thread) => props.snoozedExpanded || visible.has(keyOf(thread)));
  }, [groups.snoozed, props.visibleSnoozed, props.snoozedExpanded]);
  const visibleSettled = useMemo(() => {
    const visible = new Set(props.visibleSettled.map(keyOf));
    return groups.settled.filter(
      (thread) =>
        (hold?.section === "settled" && keyOf(thread) === hold.key) || visible.has(keyOf(thread)),
    );
  }, [groups.settled, props.visibleSettled, hold]);
  const renderedByKey = useMemo(
    () =>
      new Map(
        Object.values(groups)
          .flat()
          .map((thread) => [keyOf(thread), thread]),
      ),
    [groups],
  );
  const items = useMemo<SidebarListItem[]>(
    () => [
      { kind: "marker", marker: "pinned-header" },
      ...groups.pinned.map((thread) => ({
        kind: "thread" as const,
        key: keyOf(thread),
        section: "pinned" as const,
      })),
      { kind: "marker", marker: "pinned-divider" },
      ...groups.active.map((thread) => ({
        kind: "thread" as const,
        key: keyOf(thread),
        section: "active" as const,
      })),
      ...(groups.active.length === 0
        ? [{ kind: "marker" as const, marker: "active-placeholder" as const }]
        : []),
      ...(groups.snoozed.length > 0
        ? [{ kind: "marker" as const, marker: "snoozed-header" as const }]
        : []),
      ...visibleSnoozed.map((thread) => ({
        kind: "thread" as const,
        key: keyOf(thread),
        section: "snoozed" as const,
      })),
      { kind: "marker", marker: "settled-header" },
      ...visibleSettled.map((thread) => ({
        kind: "thread" as const,
        key: keyOf(thread),
        section: "settled" as const,
      })),
      ...(visibleSettled.length === 0
        ? [{ kind: "marker" as const, marker: "settled-placeholder" as const }]
        : []),
    ],
    [groups, visibleSnoozed, visibleSettled],
  );
  const planDrop = (key: string, targetId: string): SidebarThreadDropPlan => {
    const thread = byKey.get(key);
    const targetThread = byKey.get(targetId);
    const source = items.find((item) => item.kind === "thread" && item.key === key);
    if (!thread || source?.kind !== "thread") return { kind: "none" };
    // Organization/project identity never changes through a list gesture.
    const sameScope = (row: EnvironmentThreadShell) =>
      row.environmentId === thread.environmentId && row.projectId === thread.projectId;
    if (targetThread && !sameScope(targetThread)) return { kind: "none" };
    const scoped = all.filter(sameScope);
    const scopedKeys = new Set(scoped.map(keyOf));
    const target = resolveSidebarDropTarget(
      items.filter((item) => item.kind === "marker" || scopedKeys.has(item.key)),
      key,
      targetId,
    );
    if (!target) return { kind: "none" };
    const cap = props.capabilities(thread);
    if (target.section !== "settled" && cap?.threadSidebarBatch !== true) return { kind: "none" };
    if (source.section === "snoozed" && cap?.threadSnooze !== true) return { kind: "none" };
    if (target.section === "pinned" && cap?.threadPinning !== true) return { kind: "none" };
    return planSidebarThreadDrop({
      activeKey: key,
      activeSection: source.section,
      activePinned: thread.pinnedAt != null,
      activeSettled:
        props.isSettled?.(thread) ??
        (source.section === "settled" || thread.settledOverride === "settled"),
      supportsSettlement: cap?.threadSettlement === true,
      target,
      pinnedOrder: groups.pinned.filter(sameScope).map(keyOf),
      activeOrder: groups.active.filter(sameScope).map(keyOf),
      pinnedKeysById: new Map(scoped.map((row) => [keyOf(row), row.pinOrderKey ?? null])),
      activeKeysById: new Map(scoped.map((row) => [keyOf(row), row.activeOrderKey ?? null])),
      reorderableKeys: new Set(
        scoped.filter((row) => props.capabilities(row)?.threadPinReorder === true).map(keyOf),
      ),
      activeReorderableKeys: new Set(
        scoped.filter((row) => props.capabilities(row)?.threadActiveReorder === true).map(keyOf),
      ),
    });
  };
  const latestPlanDrop = useRef(planDrop);
  latestPlanDrop.current = planDrop;
  const collisionDetection = useMemo(
    () =>
      createSidebarCollisionDetection(
        (id) => drag !== null && latestPlanDrop.current(drag.key, id).kind !== "none",
        { items, activationY: drag?.activationY ?? null },
      ),
    [drag, items],
  );
  const draggedSettledOrder = useMemo(() => {
    const moved = drag ? byKey.get(drag.key) : undefined;
    return moved
      ? sortSettledThreadsForSidebar([
          ...groups.settled.filter((row) => keyOf(row) !== drag?.key),
          applySidebarThreadDrop(moved, "settled", new Date().toISOString()),
        ]).map(keyOf)
      : groups.settled.map(keyOf);
  }, [drag, byKey, groups.settled]);
  const strategy = useMemo(
    () =>
      createSidebarSortingStrategy({
        items,
        boundaryLabelHeight: SIDEBAR_DRAG_LABEL_HEIGHT,
        settledOrder: draggedSettledOrder,
        settledExpanded: props.settledExpanded,
        settledVisibleCount: props.visibleSettled.length,
        routeThreadKey: props.routeThreadKey,
        snoozedThreadCount: groups.snoozed.length,
        resolveTarget: (key, targetId) => {
          const plan = latestPlanDrop.current(key, targetId);
          // A stationary drag still reserves boundary-label space. Returning
          // no layout here would move hit targets as soon as a valid slot opens.
          if (plan.kind === "none") return resolveSidebarDropTarget(items, key, key);
          const section =
            plan.kind === "settle" ? "settled" : plan.kind === "move-active" ? "active" : "pinned";
          const projected = projectSidebarThreadGroups(props.groups, {
            key,
            section,
            occurredAt: new Date().toISOString(),
            assigned: sidebarDropAssignments(plan, key),
            unsettle: (plan.kind === "pin" || plan.kind === "move-active") && plan.unsettle,
          });
          return {
            section,
            pinnedOrder: projected.pinned.map(keyOf),
            activeOrder: projected.active.map(keyOf),
          };
        },
      }),
    [
      items,
      draggedSettledOrder,
      groups.snoozed.length,
      props.settledExpanded,
      props.visibleSettled.length,
      props.routeThreadKey,
      props.groups,
    ],
  );
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const attachList = useCallback((node: HTMLUListElement | null) => {
    motion.current?.dispose();
    motion.current = node ? createSidebarListMotion(node) : null;
  }, []);
  useLayoutEffect(() => {
    if (!drag) motion.current?.update(true);
  });
  useEffect(() => () => motion.current?.dispose(), []);
  const onDragEnd = (event: DragEndEvent) => {
    motion.current?.release();
    setDrag(null);
    setOverId(null);
    const key = String(event.active.id);
    const targetId = sidebarDragTargetId(event);
    const plan = targetId ? planDrop(key, targetId) : { kind: "none" as const };
    const moved = byKey.get(key);
    if (!moved || plan.kind === "none" || hold) return;
    const section =
      plan.kind === "settle" ? "settled" : plan.kind === "move-active" ? "active" : "pinned";
    const assigned = sidebarDropAssignments(plan, key);
    const scope = props.scopeKey;
    const next: Hold = {
      key,
      section,
      occurredAt: new Date().toISOString(),
      assigned,
      baseline: new Map(
        all
          .filter(
            (row) => row.projectId === moved.projectId && row.environmentId === moved.environmentId,
          )
          .map((row) => [
            keyOf(row),
            (section === "pinned" ? row.pinOrderKey : row.activeOrderKey) ?? null,
          ]),
      ),
      pending: true,
      unsettle: (plan.kind === "pin" || plan.kind === "move-active") && plan.unsettle,
    };
    setHold(next);
    const navigate = section === "settled" ? props.planForwardNavigation(key) : null;
    const ref = scopeThreadRef(moved.environmentId, moved.id);
    void (async () => {
      const run = async <A, E>(action: () => Promise<AtomCommandResult<A, E>>) => {
        if (!mounted.current || currentScope.current !== scope) throw null;
        const result = await action();
        if (result._tag === "Failure")
          throw isAtomCommandInterrupted(result) ? null : squashAtomCommandFailure(result);
      };
      try {
        if (plan.kind === "settle") {
          await run(() => actions.settleThread(ref));
        } else {
          const operations: ThreadSidebarOperation[] = [];
          const assign = (id: string, orderKey: string, pinned: boolean) => {
            const row = byKey.get(id);
            if (
              !row ||
              row.projectId !== moved.projectId ||
              row.environmentId !== moved.environmentId
            )
              throw new Error("The conversation list changed. Try the move again.");
            operations.push({
              type: pinned ? "thread.pin.reorder" : "thread.active.reorder",
              threadId: row.id,
              orderKey,
            });
          };
          if (plan.kind === "pin") {
            if (plan.unsettle)
              operations.push({ type: "thread.unsettle", threadId: ref.threadId, reason: "user" });
            operations.push({
              type: "thread.pin",
              threadId: ref.threadId,
              ...(plan.orderKey === undefined ? {} : { orderKey: plan.orderKey }),
            });
            for (const assignment of plan.extraAssignments)
              assign(assignment.id, assignment.orderKey, true);
          } else if (plan.kind === "move-active") {
            if (plan.unpin) operations.push({ type: "thread.unpin", threadId: ref.threadId });
            if (plan.unsettle)
              operations.push({ type: "thread.unsettle", threadId: ref.threadId, reason: "user" });
            if (plan.unsnooze)
              operations.push({ type: "thread.unsnooze", threadId: ref.threadId, reason: "user" });
            for (const assignment of plan.assignments)
              assign(assignment.id, assignment.orderKey, false);
          } else {
            for (const assignment of plan.assignments)
              assign(assignment.id, assignment.orderKey, true);
          }
          await run(() => actions.applySidebarThreadOperations(ref, operations));
        }
        if (mounted.current && currentScope.current === scope) {
          setHold((value) => (value === next ? { ...next, pending: false } : value));
          if (currentRoute.current === key) navigate?.();
        }
      } catch (error) {
        if (!mounted.current || currentScope.current !== scope) return;
        setHold(null);
        if (error !== null)
          toastManager.add({
            type: "error",
            title: "Could not move conversation",
            description: error instanceof Error ? error.message : "Try again.",
          });
      }
    })();
  };
  const targetSection =
    drag && overId ? resolveSidebarDropTarget(items, drag.key, overId)?.section : undefined;
  const destination =
    targetSection === "pinned"
      ? "Pin"
      : targetSection === "settled"
        ? "Settle"
        : targetSection === "active"
          ? "Move to active"
          : undefined;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={[
        restrictToVerticalAxis,
        restrictToFirstScrollableAncestor,
        (args) => restrictBelowSidebarLabel(args, SIDEBAR_DRAG_LABEL_HEIGHT),
      ]}
      onDragStart={(event) => {
        if (hold) return;
        motion.current?.suspend();
        setDrag({
          key: String(event.active.id),
          activationY:
            "clientY" in event.activatorEvent ? Number(event.activatorEvent.clientY) : null,
        });
      }}
      onDragMove={(event) => setOverId(sidebarDragTargetId(event))}
      onDragOver={(event) => setOverId(sidebarDragTargetId(event))}
      onDragCancel={() => {
        motion.current?.release();
        setDrag(null);
        setOverId(null);
      }}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={items.map(sidebarListItemId)} strategy={strategy}>
        <ul
          ref={attachList}
          role="list"
          aria-label="Conversations"
          className="relative flex flex-1 flex-col gap-px"
        >
          {items.map((item) => {
            if (item.kind === "thread") {
              const thread = renderedByKey.get(item.key);
              if (!thread) return null;
              const cap = props.capabilities(thread);
              return (
                <SortableThreadRow
                  key={item.key}
                  id={item.key}
                  disabled={
                    hold !== null ||
                    !(
                      cap?.threadPinning ||
                      cap?.threadPinReorder ||
                      cap?.threadActiveReorder ||
                      cap?.threadSettlement ||
                      cap?.threadSnooze
                    )
                  }
                >
                  {(bag) =>
                    props.renderThread(
                      thread,
                      item.section,
                      bag,
                      drag?.key === item.key ? destination : undefined,
                    )
                  }
                </SortableThreadRow>
              );
            }
            const marker = item.marker;
            if (marker === "pinned-header" || marker === "pinned-divider")
              return (
                <SidebarDragBoundary
                  key={marker}
                  marker={marker}
                  label={marker === "pinned-header" ? "Pins" : "Active"}
                  visible={drag !== null}
                  isDropTarget={
                    targetSection === (marker === "pinned-header" ? "pinned" : "active")
                  }
                />
              );
            if (marker === "active-placeholder" || marker === "settled-placeholder")
              return (
                <SidebarSectionPlaceholder
                  key={marker}
                  marker={marker}
                  label={marker === "active-placeholder" ? "Move to active" : "Settle conversation"}
                  showHint={drag !== null}
                  isDropTarget={
                    targetSection === (marker === "active-placeholder" ? "active" : "settled")
                  }
                />
              );
            const snoozed = marker === "snoozed-header";
            return (
              <SidebarSectionHeader
                key={marker}
                marker={marker}
                className={
                  // Only separate parked work from cards that actually exist.
                  // With no active/pinned threads, start the shelves at the top.
                  (groups.pinned.length > 0 || groups.active.length > 0) &&
                  (snoozed || groups.snoozed.length === 0)
                    ? "mt-auto"
                    : undefined
                }
                label={
                  snoozed
                    ? `Snoozed (${groups.snoozed.length})`
                    : `Settled (${groups.settled.length})`
                }
                dragging={drag !== null}
                isDropTarget={!snoozed && targetSection === "settled"}
                toggle={{
                  expanded: snoozed ? props.snoozedExpanded : props.settledExpanded,
                  onToggle: snoozed ? props.onToggleSnoozed : props.onToggleSettled,
                }}
              />
            );
          })}
          {props.footer}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
